import { FC, useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import type Player from 'video.js/dist/types/player'

import { Layout } from '@/components/ui'

import { useAuth } from '@/auth/useAuth'

import { useStartStopTranscribing } from '../Home/hooks/useStartStopTranscribing'

import {
	CreateHighlightFolderDialog,
	DeleteHighlightFoldersDialog,
	HighlightOverlapDialog,
	HighlightShareDialog,
	LinksDialog,
	LiveOriginalColumn,
	LiveSidebar,
	LiveTranslationColumn,
	SelectedSegmentFolderMenu
} from './components'
import { getHighlightSummary } from './helpers'
import {
	useCreateFolderFromSegment,
	useDeleteHighlightFolders,
	useGenerate,
	useHighlightFolderSelection,
	useHighlightSegmentActions,
	useHighlightSegmentSelection,
	useHighlightShareDialog,
	useHighlightSharing,
	useHighlights,
	useKeywordSearch,
	useLive,
	useLiveTranscriptionPolling,
	useTranscriptSelectionShortcut,
	useTranscriptionScrollSync
} from './hooks'

const Live: FC = () => {
	const {
		task,
		refetchTask,
		isTaskLoading,
		transcriptions,
		isTranscriptionsLoading,
		refetchTranscriptions,
		uploadHighlight,
		isUploadHighlightLoading
	} = useLive()

	const {
		folders,
		createHighlightFolder,
		isFolderCreating,
		updateHighlightFolder,
		isFolderUpdating,
		deleteFolder,
		isDeleteFolderLoading,
		addSegmentToFolder,
		isHighlightAdding,
		deleteHighlight,
		isHighlightDeleting
	} = useHighlights(task?.id)

	const {
		selectSegmentFromTranscript,
		selectSegmentFromTimeRange,
		pendingSegment,
		highlightMenu,
		closeHighlightMenu
	} = useHighlightSegmentSelection()

	const {
		saveSelectedSegmentToFolders,
		moveSegmentToFolder,
		isOverlapWarningOpen,
		closeOverlapWarning,
		confirmOverlapWarning
	} = useHighlightSegmentActions({
		folders,
		pendingSegment,
		closeHighlightMenu,
		addSegmentToFolder,
		deleteHighlight
	})

	const {
		generatedResults,
		isGeneratedResultsOpen,
		closeGeneratedResults,
		isJobLoading,
		jobStatus,
		downloadSegment,
		downloadFolder,
		downloadSelectedFolders
	} = useGenerate({
		taskId: task?.id,
		uploadHighlight
	})

	const {
		keywords,
		keywordMatches,
		isKeywordSearching,
		addKeyword,
		removeKeyword
	} = useKeywordSearch(
		task?.id,
		task?.source_type === 'listener' && task?.status === 'in_progress'
	)

	const {
		containerRef,
		translatedRef,
		collectOriginalRef,
		collectTranslatedRef,
		handleScroll,
		scrollToSegment
	} = useTranscriptionScrollSync(transcriptions)

	const { startStopTranscribing } = useStartStopTranscribing()

	const { createShareLink, isShareLinkCreating } = useHighlightSharing()

	const {
		isSelectMode,
		setIsSelectMode,
		selectedFolderIds,
		setSelectedFolderIds,
		toggleFolderSelection,
		cancelFolderSelection,
		visibleFolders
	} = useHighlightFolderSelection(folders)

	const {
		deleteFoldersModal,
		openDeleteFolderModal,
		openDeleteSelectedFoldersModal,
		confirmDeleteFolders,
		closeDeleteFoldersModal
	} = useDeleteHighlightFolders({
		folders,
		selectedFolderIds,
		setSelectedFolderIds,
		setIsSelectMode,
		deleteFolder
	})

	const {
		newFolderName,
		setNewFolderName,
		folderNameError,
		setFolderNameError,
		createFolderFromSegmentModal,
		openCreateFolderFromSegment,
		closeCreateFolderFromSegment,
		saveFolderFromSegment
	} = useCreateFolderFromSegment({
		taskId: task?.id,
		folders,
		pendingSegment,
		createHighlightFolder,
		closeHighlightMenu
	})

	const {
		shareUrl,
		shareLinkModal,
		shareSelectedFolders,
		shareFolder,
		copyShareLink
	} = useHighlightShareDialog({
		selectedFolderIds,
		createShareLink
	})

	const navigate = useNavigate()
	const { id } = useParams()
	const { isInt } = useAuth()

	const [isCollapsed, setIsCollapsed] = useState(false)
	const [timeEnd, setTimeEnd] = useState('')
	const [timeStart, setTimeStart] = useState('')
	const [isLoading, setIsLoading] = useState(true)

	const [isRendered, setIsRendered] = useState(false)

	const playerRef = useRef<Player | null>(null)

	const isHighlightListUpdating =
		isFolderCreating ||
		isFolderUpdating ||
		isDeleteFolderLoading ||
		isHighlightAdding ||
		isHighlightDeleting

	useLiveTranscriptionPolling({
		sourceType: task?.source_type,
		status: task?.status,
		refetchTranscriptions
	})

	useTranscriptSelectionShortcut({
		enabled: !!id,
		isBlocked: isJobLoading || isUploadHighlightLoading,
		transcriptions,
		containerRef,
		translatedRef,
		selectSegmentFromTranscript
	})

	useEffect(() => {
		if (task?.status === 'in_progress' && translatedRef.current && isRendered) {
			translatedRef.current.scrollTo({
				top: translatedRef.current.scrollHeight,
				behavior: 'smooth'
			})
		}
	}, [task?.status, isRendered])

	useEffect(() => {
		if (!id) {
			navigate('/')
		}
	}, [id, navigate])

	useEffect(() => {
		if (!isTaskLoading && !isTranscriptionsLoading) {
			setIsLoading(false)
		}
	}, [isTaskLoading, isTranscriptionsLoading])

	useEffect(() => {
		if (highlightMenu === null) {
			setTimeStart('')
			setTimeEnd('')
		}
	}, [highlightMenu])

	const { totalHighlightsAmount, totalTimeHighlights, allFolderIds } = useMemo(
		() => getHighlightSummary(folders),
		[folders]
	)

	const handleSeek = (seconds: number) => {
		if (playerRef.current) {
			playerRef.current.currentTime(seconds)
		}
	}

	const handleTranscriptionRendered = useCallback(() => {
		setIsRendered(true)
	}, [])

	const toggleStartStop = async () => {
		await startStopTranscribing({
			proxy_name: task.source,
			blocked: task.status !== 'stopped'
		})
		await refetchTask()
	}

	const isTranscribingStopped = task?.status === 'stopped'

	const handleAddManualSegment = (position: { x: number; y: number }) => {
		selectSegmentFromTimeRange(
			timeStart,
			timeEnd,
			transcriptions.transcriptions.translation,
			position
		)
	}

	return (
		<Layout className='flex h-screen w-full'>
			{/* Sidebar */}
			<LiveSidebar
				task={task}
				isCollapsed={isCollapsed}
				isLoading={isLoading}
				isUploadHighlightLoading={isUploadHighlightLoading}
				isJobLoading={isJobLoading}
				isShareLinkCreating={isShareLinkCreating}
				jobStatus={jobStatus}
				onBack={() => navigate('/home')}
				onPlayerReady={player => {
					playerRef.current = player
				}}
				controlsProps={{
					timeEnd,
					timeStart,
					setTimeEnd,
					setTimeStart,
					isSelectMode,
					toggleSelectedMode: setIsSelectMode,
					onCancelSelection: cancelFolderSelection,
					createFolder: createHighlightFolder,
					deleteSelectedFolders: openDeleteSelectedFoldersModal,
					disabled: !selectedFolderIds.length,
					onAddManualSegment: handleAddManualSegment,
					downloadSelectedFolders: () =>
						downloadSelectedFolders({
							folders,
							selectedFolderIds
						}),
					folders,
					shareSelectedFolders,
					keywords,
					isKeywordSearching,
					onAddKeyword: addKeyword,
					onRemoveKeyword: removeKeyword
				}}
				listProps={{
					folders,
					deleteHighlight,
					onOpenDeleteFolder: openDeleteFolderModal,
					updateFolder: updateHighlightFolder,
					isUpdating: isHighlightListUpdating,
					isSelectMode,
					selectedFolderIds,
					onToggleFolder: toggleFolderSelection,
					moveSegmentToFolder,
					downloadFolder,
					downloadSegment,
					shareFolder,
					onScrollToSegment: scrollToSegment
				}}
				footerProps={{
					disabled:
						isLoading ||
						!totalHighlightsAmount?.length ||
						isUploadHighlightLoading ||
						isJobLoading ||
						isHighlightListUpdating,
					generateAllFolders: () =>
						downloadSelectedFolders({
							folders,
							selectedFolderIds: allFolderIds
						}),
					totalTimeHighlights
				}}
			/>

			{/* Original transcription block */}
			<LiveOriginalColumn
				isCollapsed={isCollapsed}
				isLoading={isLoading}
				onCollapse={() => setIsCollapsed(true)}
				containerRef={containerRef}
				transcriptions={transcriptions}
				textProps={{
					folders: visibleFolders,
					refCollector: collectOriginalRef,
					keywordMatches
				}}
				segmentMenuProps={
					highlightMenu?.strategy === 'absolute' &&
					highlightMenu.container === 'original' &&
					pendingSegment
						? {
								folders,
								highlightMenu,
								onSave: saveSelectedSegmentToFolders,
								onCreateFolder: openCreateFolderFromSegment
						  }
						: null
				}
			/>

			{/* Translation block */}
			<LiveTranslationColumn
				isCollapsed={isCollapsed}
				isLoading={isLoading}
				showStopButton={!isInt && task?.status === 'in_progress'}
				onStop={toggleStartStop}
				onExpandOriginal={() => setIsCollapsed(false)}
				containerRef={translatedRef}
				onScroll={handleScroll}
				transcriptions={transcriptions}
				textProps={{
					refCollector: collectTranslatedRef,
					onRendered: handleTranscriptionRendered,
					onSeek: handleSeek,
					folders: visibleFolders,
					isTranscribingStopped,
					taskId: id,
					keywordMatches
				}}
				segmentMenuProps={
					highlightMenu?.strategy === 'absolute' &&
					highlightMenu.container === 'translation' &&
					pendingSegment
						? {
								folders,
								highlightMenu,
								onSave: saveSelectedSegmentToFolders,
								onCreateFolder: openCreateFolderFromSegment
						  }
						: null
				}
			/>

			{highlightMenu?.strategy === 'fixed' && pendingSegment && (
				<SelectedSegmentFolderMenu
					folders={folders}
					highlightMenu={highlightMenu}
					onSave={saveSelectedSegmentToFolders}
					onCreateFolder={openCreateFolderFromSegment}
				/>
			)}

			{/* Dialog windows section */}

			{/* Create new folder for segments */}
			<CreateHighlightFolderDialog
				open={createFolderFromSegmentModal.open}
				name={newFolderName}
				error={folderNameError}
				onNameChange={value => {
					setNewFolderName(value)
					setFolderNameError('')
				}}
				onClose={closeCreateFolderFromSegment}
				onCreate={saveFolderFromSegment}
			/>

			{/* Delete folder */}
			<DeleteHighlightFoldersDialog
				open={deleteFoldersModal.open}
				onClose={closeDeleteFoldersModal}
				onDelete={confirmDeleteFolders}
			/>

			{/* Links for MP4 and SRT files downloading */}
			<LinksDialog
				open={isGeneratedResultsOpen}
				onClose={closeGeneratedResults}
				results={generatedResults}
			/>

			{/* Link for shared folders */}
			<HighlightShareDialog
				open={shareLinkModal.open}
				shareUrl={shareUrl}
				onClose={shareLinkModal.onClose}
				onCopy={copyShareLink}
			/>

			{/* Overlapping modal window */}
			<HighlightOverlapDialog
				open={isOverlapWarningOpen}
				onClose={closeOverlapWarning}
				onConfirm={confirmOverlapWarning}
			/>
		</Layout>
	)
}

export default Live
